import { Link, createFileRoute } from "@tanstack/react-router";
import {
	Activity,
	AlertTriangle,
	ArrowRight,
	CheckCircle2,
	ClipboardCheck,
	Database,
	FlaskConical,
	LineChart,
	Network,
	Target,
	Wrench,
} from "lucide-react";
import type { ReactNode } from "react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/")({
	component: HomePage,
});

function HomePage() {
	return (
		<div className="mx-auto flex max-w-6xl flex-col gap-6">
			<section className="flex flex-col gap-3">
				<div className="inline-flex w-fit items-center gap-2 rounded-md border border-surface-border bg-surface-card px-3 py-1 text-xs font-medium text-text-secondary">
					<Activity className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
					运营治理升级 · 当前可验收
				</div>
				<PageHeader
					title="运营总览"
					description="先从闭环状态判断当前能看清什么、还能补什么，再进入单条应答测试、接口字段、局部资产和评测运营。"
					help={homeHelp}
				>
					<Button asChild>
						<Link to="/plan-lab" hash="overview">
							<Network className="mr-2 h-4 w-4" aria-hidden="true" />
							打开链路总览
						</Link>
					</Button>
					<Button asChild variant="outline">
						<Link to="/pipeline-testbench">
							<ArrowRight className="mr-2 h-4 w-4" aria-hidden="true" />
							进入链路测试台
						</Link>
					</Button>
				</PageHeader>
			</section>

			<P7GovernanceSummary />

			<section className="grid gap-4 md:grid-cols-3">
				<StatusCard
					icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
					title="线上 API"
					status="可读"
					description="后端健康接口和 admin/v1 数据接口在线，前端改为同源请求后可直接读取真实数据。"
				/>
				<StatusCard
					icon={<FlaskConical className="h-4 w-4" aria-hidden="true" />}
					title="链路测试"
					status="可验证"
					description="链路测试台可看到主 Agent、知识召回、质量检查、人工和 Shopify 读写的真实落地状态。"
				/>
				<StatusCard
					icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
					title="评测闭环"
					status="闭环 MVP"
					description="评测运行、问题样本、对比快照和批量回归调度/执行/关闭首版已落地；后台异步队列、完整 A/B 和线上候选入口继续后续。"
				/>
			</section>

			<section className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
				<Card>
					<CardHeader>
						<CardTitle>真实链路边界</CardTitle>
						<CardDescription>用于判断当前页面结果属于真实、沙盒还是受限状态。</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-3 md:grid-cols-2">
							<BoundaryItem label="Shopify shop/product 已接通" state="真实只读" tone="success" />
							<BoundaryItem label="主 Agent 真实 LLM 回复" state="已接入" tone="success" />
							<BoundaryItem label="订单、会话和人工闭环" state="待补齐" tone="warning" />
							<BoundaryItem label="RAG runtime 与质量门禁" state="待闭环" tone="warning" />
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>下一步验收</CardTitle>
						<CardDescription>按产品走查、QA 验证和上线后检查完成验收。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3 text-sm text-text-secondary">
						<div className="flex gap-3">
							<Database
								className="mt-0.5 h-4 w-4 flex-none text-brand-primary"
								aria-hidden="true"
							/>
							<p>先用产品指南跑通地图、评测运营、链路测试台和运行记录的闭环故事。</p>
						</div>
						<div className="flex gap-3">
							<CheckCircle2
								className="mt-0.5 h-4 w-4 flex-none text-brand-primary"
								aria-hidden="true"
							/>
							<p>再按 QA 清单补命令结果、发布检查和线上 smoke 证据。</p>
						</div>
					</CardContent>
				</Card>
			</section>
		</div>
	);
}

function P7GovernanceSummary() {
	const loopSteps = [
		{ label: "感知", icon: Activity },
		{ label: "观测", icon: Network },
		{ label: "判断", icon: Target },
		{ label: "执行", icon: Wrench },
		{ label: "对比效果", icon: LineChart },
	];

	const baselines = [
		"接口文档套件和字段索引",
		"观测覆盖矩阵",
		"左侧菜单、首页摘要和地图入口",
		"评测运行 / 问题样本 / 状态时间线",
		"对比快照、批量回归调度/执行/关闭",
	];

	const openItems = [
		"节点点击埋点",
		"后台异步队列",
		"完整 A/B 与线上候选入口",
		"审核队列 / 策略配置",
		"自动血缘生成",
		"资产变更审计",
	];

	return (
		<section
			className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]"
			data-testid="p7-governance-summary"
		>
			<Card>
				<CardHeader className="space-y-4">
					<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
						<div>
							<CardTitle className="text-lg">闭环治理状态</CardTitle>
							<CardDescription className="mt-2 max-w-3xl leading-6">
								当前不是新增一个孤立页面，而是把接口字段、观测、数据流向、测试台和评测运营串成一条可复盘的运营闭环。
							</CardDescription>
						</div>
						<Button asChild size="sm">
							<Link to="/plan-lab" hash="overview">
								<Network className="mr-2 h-4 w-4" aria-hidden="true" />
								打开链路总览
							</Link>
						</Button>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-2 sm:grid-cols-5">
						{loopSteps.map((step, index) => {
							const Icon = step.icon;
							return (
								<div
									key={step.label}
									className="min-h-[88px] rounded-md border border-surface-border bg-surface-background px-3 py-3"
								>
									<div className="flex items-center gap-2">
										<span className="flex h-8 w-8 items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
											<Icon className="h-4 w-4" aria-hidden="true" />
										</span>
										<span className="text-xs font-medium text-text-muted">
											{String(index + 1).padStart(2, "0")}
										</span>
									</div>
									<div className="mt-3 text-sm font-semibold text-text-primary">{step.label}</div>
								</div>
							);
						})}
					</div>

					<div className="grid gap-3 md:grid-cols-2">
						<GovernanceList
							icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
							title="已完成"
							items={baselines}
							tone="success"
						/>
						<GovernanceList
							icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
							title="未完成"
							items={openItems}
							tone="warning"
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">当前推进</CardTitle>
					<CardDescription>这次先把闭环治理收到可验收、可讲清、可交接。</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<FocusItem
						icon={<FlaskConical className="h-4 w-4" aria-hidden="true" />}
						title="菜单与页面结构"
						description="左侧菜单已按运营总览、接入状态、应答测试、意图分类、评测运营、接口与字段等核心环节重排，老路由保留。"
					/>
					<FocusItem
						icon={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
						title="下一步"
						description="先做视觉和可用性收口，再进入字段一致性治理。"
					/>
				</CardContent>
			</Card>
		</section>
	);
}

function GovernanceList({
	icon,
	title,
	items,
	tone,
}: {
	icon: ReactNode;
	title: string;
	items: string[];
	tone: "success" | "warning";
}) {
	const toneClass =
		tone === "success"
			? "border-brand-primary/30 bg-brand-primary/10 text-brand-primary"
			: "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";

	return (
		<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
				<span className={`flex h-7 w-7 items-center justify-center rounded-md border ${toneClass}`}>
					{icon}
				</span>
				{title}
			</div>
			<ul className="mt-3 space-y-2 text-sm leading-6 text-text-secondary">
				{items.map((item) => (
					<li key={item} className="flex gap-2">
						<span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary" />
						<span>{item}</span>
					</li>
				))}
			</ul>
		</section>
	);
}

function FocusItem({
	icon,
	title,
	description,
}: { icon: ReactNode; title: string; description: string }) {
	return (
		<div className="flex gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
				{icon}
			</span>
			<div>
				<div className="text-sm font-semibold text-text-primary">{title}</div>
				<p className="mt-1 text-sm leading-6 text-text-secondary">{description}</p>
			</div>
		</div>
	);
}

const homeHelp: PageHelp = {
	title: "运营总览说明",
	description: "这里用来快速判断 P7 闭环治理的完成状态，以及哪些入口能继续支撑测试、归因和复盘。",
	sections: [
		{
			title: "什么时候看",
			items: [
				"每天开始研发或验收前，先确认线上 API、链路测试和外部依赖状态。",
				"上线后排查页面异常时，先看这里区分系统故障和外部权限限制。",
			],
		},
		{
			title: "状态怎么理解",
			items: [
				"真实只读表示系统已经能读取真实店铺事实。",
				"受限表示外部审批或配置未完成，不应当被当作产品失败。",
				"保护中表示写操作只跑 dry-run 或人工确认流程。",
			],
		},
	],
};

function StatusCard({
	icon,
	title,
	status,
	description,
}: {
	icon: ReactNode;
	title: string;
	status: string;
	description: string;
}) {
	return (
		<Card>
			<CardHeader className="space-y-3">
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-2 text-sm font-medium text-text-primary">
						<span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
							{icon}
						</span>
						{title}
					</div>
					<span className="rounded-md bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary">
						{status}
					</span>
				</div>
				<CardDescription className="leading-6">{description}</CardDescription>
			</CardHeader>
		</Card>
	);
}

function BoundaryItem({
	label,
	state,
	tone,
}: {
	label: string;
	state: string;
	tone: "success" | "warning" | "neutral";
}) {
	const toneClass =
		tone === "success"
			? "border-brand-primary/30 bg-brand-primary/10 text-brand-primary"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: "border-surface-border bg-surface-subtle text-text-secondary";

	return (
		<div className="flex items-center justify-between gap-3 rounded-md border border-surface-border bg-surface-background px-3 py-3">
			<span className="text-sm font-medium text-text-primary">{label}</span>
			<span className={`rounded-md border px-2 py-1 text-xs font-medium ${toneClass}`}>
				{state}
			</span>
		</div>
	);
}
